<template>
  <div class="table-container">
    <div class="table-controls d-flex justify-content-end pt-2 pb-4">
      <Preloader v-if="isDataPageLoading" />
      <div class="table-controls-item filter-datepicker">
        <b-button
          variant="light"
          :disabled="disablePrevMonth"
          @click.prevent="changeMonth('prev')"
        >‹</b-button>
        <Datepicker
          :bootstrap-styling="true"
          :value="_toIsoStringTime(selectedYm)"
          :format="config.dateFormatDatePicker"
          :disabledDates="{ to: new Date(fromDateToUTC(fromYm,'YYYY-MM')), from: new Date() }"
          minimum-view="month"
          @selected="selectMonth"
        />
        <b-button
          variant="light"
          :disabled="disableNextMonth"
          @click.prevent="changeMonth('next')"
        >›</b-button>
      </div>
      <div class="table-controls-right">
        <div class="table-controls-right__item">
          <b-button
            class="ml-auto"
            size="sm"
            @click.prevent="isShowAllFields = !isShowAllFields"
          >
            {{ isShowAllFields ? "Hide some fields" : "Show all fields" }}
          </b-button>
        </div>
      </div>
    </div>
    <div class="position-relative" ref="tableContain">
      <b-table-simple
        v-if="reportDataBySelectedYM.itemsByManager.length"
        class="table__theme-default table-sales-report"
        :fixed="false"
        :hover="false"
        stripped
        responsive
      >
        <b-thead>
          <b-tr class="thead-row row-top text-center" :class="{'sticky-head': getCoordinates > 0}" :style="{'top': getCoordinates + 'px'}">
            <template v-for="field in getCurrentFields">
              <b-th :key="field.key" :class="field.class" :style="{'min-width': thsWidth[`userth${field.key}`] + 'px', 'width': (field.width ? field.width + 'px' : '')}">{{ field.label }}</b-th>
            </template>
          </b-tr>
          <b-tr class="text-center">
            <template v-for="field in getCurrentFields">
              <b-th :key="field.key" :class="field.class" :style="{'width': (field.width ? field.width + 'px' : ''), 'min-width': (field.minWidth ? field.minWidth + 'px' : '')}"><div :ref="`userth${field.key}`">{{ field.label }}</div></b-th>
            </template>
          </b-tr>
        </b-thead>
        <b-tbody>
          <template v-for="(managerData) in reportDataBySelectedYM.itemsByManager">
            <b-tr class="text-center mb-4 font-weight-bold" :key="`${managerData.managerName}`">
              <b-td :colspan="getCurrentFields.length" class="manager-name">{{ managerData.managerName }}</b-td>
            </b-tr>
            <b-tr class="text-center" v-for="(projectData, i) in managerData.projects" :key="`${managerData.managerName}-${i}`">
              <b-td>
                <div class="d-flex text-left">
                  <b-button
                    class="button hp-btn d-inline-block mr-2"
                    :variant="'secondary'"
                    size="sm"
                    v-b-tooltip.hover="{ variant: 'base' }"
                    :title="'Hours and payments report'"
                    @click="getSlideOut(projectData)"
                  >
                    HP
                  </b-button>
                  <TableProjectItem
                    :number="i"
                    :link="projectData.projectKey"
                    :project-name="projectData.projectName"
                    :project-subtitle="projectData.projectSubtitle ? projectData.projectSubtitle : ''"
                    :project-clients="projectData.clientName"
                    view="table-row"
                  />
                </div>
              </b-td>
              <b-td
                class="text-truncate"
                :style="{'max-width': (getField('channel').width ? getField('channel').width + 'px' : '')}"
                v-b-tooltip.hover="{ variant: 'base' }"
                :title="projectData.salesChannel"
              >{{ projectData.salesChannel }}</b-td>
              <b-td
                class="text-truncate"
                :style="{'max-width': (getField('pre_sales').width ? getField('pre_sales').width + 'px' : '')}"
                v-b-tooltip.hover="{ variant: 'base' }"
                :title="projectData.preSalesManager"
              >{{ projectData.preSalesManager ? projectData.preSalesManager : '' }}</b-td>
              <b-td>
                <b-tr
                  class="d-block border-1 employee-row"
                  :variant="getEmployeeRowVariant(projectData, employee)"
                  v-for="employee in projectData.employees"
                  :key="employee.name"
                >
                  {{ employee.name }}<span v-if="employee.startDate"> ({{ employee.startDate }})</span>
                </b-tr>
              </b-td>
              <b-td
                :style="{'max-width': (getField('comment').width ? getField('comment').width + 'px' : '')}"
              >
                <div class="position-relative pr-1">
                  <TextEditor
                    class="text-left"
                    show-text
                    data-already-loaded
                    empty-text=""
                    :entity-type="commentEntityTypes.SalesReportProject"
                    :entity-id="projectData.projectId"
                  />
                </div>
              </b-td>
              <b-td v-if="isShowAllFields" class="p-0">
                <b-tr class="d-block employee-row" v-for="employee in projectData.employees" :key="employee.name">
                  <div
                    v-b-tooltip.hover="{ variant: 'base' }"
                    :title="employee.name + ': ' + getPlannedByTooltip(employee.paidAmount, employee.plannedAmount)"
                    :class="{'not-paid': employee.plannedAmount > employee.paidAmount }"
                  >{{ employee.plannedAmount }}</div>
                </b-tr>
              </b-td>
              <b-td v-if="isShowAllFields" class="p-0">
                <b-tr class="d-block employee-row" v-for="employee in projectData.employees" :key="employee.name">
                  <div v-b-tooltip.hover="{ variant: 'base' }" :title="employee.name">{{ employee.paidAmount }}</div>
                </b-tr>
              </b-td>
              <b-td
                v-b-tooltip.hover="{ variant: 'base' }"
                :title="getPlannedByTooltip(projectData.paidAmount, projectData.plannedAmount)">
                <div :class="{'not-paid': projectData.plannedAmount > projectData.paidAmount }">{{ projectData.plannedAmount }}</div>
              </b-td>
              <b-td>{{ projectData.paidAmount }}</b-td>
              <b-td>{{ projectData.commission }}</b-td>
              <b-td>
                <InvoicesLinksItems
                  v-if="projectData.invoices"
                  :invoice-key="managerData.managerName + ' - ' + projectData.projectName"
                  :invoices-data="projectData.invoices"
                  :quantity-show-invoices="1"
                  :templates="getTemplates"
                  invoice-modal-class="invoices-modal"
                  @reloadInvoices="reloadPage"
                />
              </b-td>
            </b-tr>
            <b-tr class="manager-total text-center mb-4" :key="`${managerData.managerName}-total`">
              <b-td class="sales-commission-formula text-left border-0" :colspan="getCurrentFields.length - 5">
                <div v-show="isShowAllFields">
                  <span class="font-weight-bold">Sales Commission</span>
                  <span v-b-tooltip.hover="{ variant: 'base', customClass: 'wide-tooltip' }" :title="managerData.salesCommissionFormulaInfo">
                    ({{ managerData.managerName }}, $):
                    <br>{{ managerData.salesCommissionFormula }}
                  </span>
                </div>
              </b-td>
              <b-td>Total</b-td>
              <b-td class="planned-total">{{ managerData.plannedTotal }}</b-td>
              <b-td class="paid-total">{{ managerData.paidTotal }}</b-td>
            </b-tr>
            <b-tr class="spacer" :key="`${managerData.managerName}-space`"/>
          </template>

          <b-tr class="summary-total text-center mb-4 border-0">
            <b-td class="text-left border-0" :colspan="getCurrentFields.length - 5"></b-td>
            <b-td>Grand total</b-td>
            <b-td class="planned-total">{{ summaryPlannedTotal }}</b-td>
            <b-td class="paid-total">{{ summaryPaidTotal }}</b-td>
          </b-tr>

          <b-tr class="mb-4" v-if="Object.values(reportDataBySelectedYM.preSalesCommissionFormulas).length && isShowAllFields">
            <b-td class="text-left border-0" :colspan="getCurrentFields.length">
              <div
                class="mb-1"
                v-for="(preSalesCommissionFormula, preSalesManagerName) in reportDataBySelectedYM.preSalesCommissionFormulas"
                :key="`preSales-${preSalesManagerName}`">
                <span class="font-weight-bold">Pre-Sales Commission</span>
                <span v-b-tooltip.hover="{ variant: 'base', customClass: 'wide-tooltip' }" :title="preSalesCommissionFormula['info']">
                  ({{ preSalesManagerName ? preSalesManagerName : 'Not selected' }}, $): {{ preSalesCommissionFormula['formula'] }}
                </span>
              </div>
            </b-td>
          </b-tr>
        </b-tbody>
      </b-table-simple>
      <div v-else class="text-center"><h5>No data</h5></div>
    </div>
    <transition name="show">
      <div class="slide-out__container" v-if="showSlideOut">
        <button
          type="button"
          aria-label="Close"
          class="close close-slideout"
          @click="closeSlideOut()"
        >
            <span class="material-icons">
              close
            </span>
        </button>
        <TableWithDetails :current-project="openProject" />
      </div>
    </transition>
  </div>
</template>

<script>
import { commentEntityTypes } from '@mocks/comment-entity-types'
import { cloneDeep } from 'lodash'
import Moment from 'moment'
import Datepicker from 'vuejs-datepicker'
import { mapActions } from 'vuex'
import StickyHeaderMixin from '@/mixins/StickyHeaderMixin'
import TextEditor from '@components/TextEditor'
import Preloader from '@components/Preloader'
import InvoiceFormModal from '../../mixins/InvoiceFormModal'
import TableProjectItem from '@components/tables/TableProjectItem'
import InvoicesLinksItems from '@components/InvoicesLinksItems'
import TableWithDetails from '@components/tables/TableWithDetails'

const currentYm = Moment().format('YYYY-MM')

export default {
  components: {
    Datepicker,
    TextEditor,
    Preloader,
    TableProjectItem,
    InvoicesLinksItems,
    TableWithDetails
  },
  mixins: [StickyHeaderMixin, InvoiceFormModal],
  data () {
    return {
      reportData: {},
      reportDataBySelectedYM: {
        itemsByManager: [],
        preSalesCommissionFormulas: []
      },
      isDataPageLoading: false,
      currentFields: [
        {
          key: 'info',
          label: 'Project and client',
          width: 120
        },
        {
          key: 'channel',
          label: 'Channel',
          width: 80
        },
        {
          key: 'pre_sales',
          label: 'Pre sales',
          width: 85
        },
        {
          key: 'employee',
          label: 'Employee',
          width: 195,
          minWidth: 195
        },
        {
          key: 'comment',
          label: 'Comment',
          width: 190,
          minWidth: 190
        },
        {
          key: 'planned_by_employee',
          label: 'Planned by employee $',
          width: 100
        },
        {
          key: 'paid_by_employee',
          label: 'Paid by employee $',
          width: 100
        },
        {
          key: 'planned',
          label: 'Planned $',
          class: 'planned-total',
          width: 90
        },
        {
          key: 'paid',
          label: 'Paid $',
          class: 'paid-total',
          width: 90
        },
        {
          key: 'commission',
          label: 'Comm',
          width: 60
        },
        {
          key: 'invoices',
          label: 'Invoices',
          width: 120
        }
      ],
      invoicesLoading: false,
      isShowAllFields: false,
      salesCommissionSum: {},
      preSalesCommissionSum: {},
      preSalesList: {},
      fromYm: '2021-02',
      selectedYm: currentYm,
      config: {
        dateFormat: 'YYYY-MM',
        dateFormatDatePicker: 'yyyy-MM'
      },
      editInvoices: {
        show: false,
        title: 'Edit Invoice',
        submitButtonText: 'Edit',
        form: null
      },
      showSlideOut: false,
      openProject: null
    }
  },
  computed: {
    getActualProjects () {
      return this.getProjects
        .filter(project => project.project_status.name !== 'done')
    },
    getCurrentFields () {
      const currentTableFields = cloneDeep(this.currentFields)
      if (this.isShowAllFields) {
        return currentTableFields
      }
      const plannedByEmployeeIndex = currentTableFields.findIndex(item => item.key === 'planned_by_employee')
      const paidByEmployeeIndex = currentTableFields.findIndex(item => item.key === 'paid_by_employee')

      currentTableFields.splice(plannedByEmployeeIndex, 1)
      currentTableFields.splice(paidByEmployeeIndex - 1, 1)

      return currentTableFields
    },
    disableNextMonth () {
      return this.selectedYm === currentYm
    },
    disablePrevMonth () {
      return this.fromYm === this.selectedYm
    },
    commentEntityTypes () {
      return commentEntityTypes
    },
    summaryPlannedTotal () {
      return this.roundValue(this.reportDataBySelectedYM.itemsByManager.reduce((sum, managerData) => sum + managerData.plannedTotal, 0))
    },
    summaryPaidTotal () {
      return this.roundValue(this.reportDataBySelectedYM.itemsByManager.reduce((sum, managerData) => sum + managerData.paidTotal, 0))
    }
  },
  methods: {
    ...mapActions({
      fetchComments: 'comments/fetchComments'
    }),
    async reloadPage () {
      this.reportData = {}
      this.isDataPageLoading = true
      this.reportData[this.selectedYm] = await this.fetchSalesReportData(this.selectedYm)
      this.reportDataBySelectedYM = this.reportData[this.selectedYm]
    },
    selectMonth (data) {
      this.selectedYm = this._toLocalTime(data, this.config.dateFormat)
    },
    changeMonth (type) {
      const currentMonth = Moment(
        this.selectedYm,
        this.config.dateFormat
      )
      let newYm

      if (type === 'next') {
        newYm = currentMonth.add(1, 'months').format(this.config.dateFormat)
      } else {
        newYm = currentMonth
          .subtract(1, 'months')
          .format(this.config.dateFormat)
      }

      this.selectedYm = newYm
    },
    getSlideOut (project) {
      this.showSlideOut = true
      this.openProject = {
        id: project.projectId,
        name: project.projectName,
        key: project.projectKey
      }
      window.scrollTo(0, 0)
    },
    closeSlideOut () {
      this.showSlideOut = false
      this.openProject = null
    },
    async fetchSalesReportData (dateYm) {
      return this._toggleDataPageLoading(async () => {
        this.fetchComments({ type: commentEntityTypes.SalesReportProject })
        return this.$api.finances
          .getReportsSales({ dateYm })
          .then((response) => {
            return response.data
          })
      })
    },
    getEmployeeRowVariant (project, employee) {
      if (employee.paidAmount === 0 || project.preSalesManager.length === 0) {
        return 'light'
      } else if (employee.loggedHours < 168) {
        return 'primary'
      } else if (employee.paidAmount >= 1 && employee.loggedHours >= 168) {
        return 'secondary'
      }
    },
    getPlannedByTooltip (paidAmount, plannedAmount) {
      const tooltipParts = []
      if (paidAmount > 0) {
        tooltipParts.push(paidAmount + ' paid')
      }
      if (plannedAmount > paidAmount) {
        tooltipParts.push(this.roundValue(plannedAmount - paidAmount) + ' not paid')
      }
      return tooltipParts.join(' / ')
    },
    getField (name) {
      const currentTableFields = cloneDeep(this.currentFields)
      return currentTableFields.find(item => item.key === name)
    },
    roundValue (value) {
      return Math.round(value * 100) / 100
    }
  },
  watch: {
    'selectedYm': async function (dateYm) {
      if (this.reportData[dateYm] === undefined) {
        this.reportData[dateYm] = await this.fetchSalesReportData(dateYm)
      }
      this.reportDataBySelectedYM = this.reportData[dateYm]
    }
  },
  async created () {
    this.reportData[this.selectedYm] = await this.fetchSalesReportData(this.selectedYm)
    this.reportDataBySelectedYM = this.reportData[this.selectedYm]
    await this.fetchInvoiceFormData()
  }
}
</script>

<style lang="scss" scoped>
.table-container {
  padding: 10px;
  border-radius: 10px;
  background-color: #fff;

  .preloader {
    background-color: transparent;
  }

  .table-text {
    font-size: 12px;
    font-weight: 500;
    color: #7489A9;
  }
  .table-sales-report .manager-name {
    font-size: 1rem;
    font-weight: bold;
    border: 0px;
  }
  .manager-border {
    border-top: 3px solid rgba(0,0,0,.3);

    td {
      padding-top: 40px;
    }
  }
  .manager-total {
    font-weight: bold;
    border: none !important;
    .sales-commission-formula {
      font-weight: normal;
    }
  }
  .summary-total {
    font-weight: bold;
    font-size: 1.5em;
  }
  .planned-total {
    color: limegreen
  }
  .paid-total {
    color: red
  }
  .not-paid {
    background-color: #ede742;
  }
  .employee-row {
    padding: 7px 0;
  }
  .editor-block {
    top: 50%;
    right: 0;
    transform: translate(-50%, -50%)
  }
}
.thead-row {
  display: none;
  &.sticky-head {
    height: 50px;
    padding: 10px 0;
    display: table-row;
    align-items: center;
    background-color: #fff;
    position: relative;
    left: 0;
    right: 0;
    z-index: 9;

    .sticky-head__second-line {
      span {
        padding: 6px 5px 10px;

        &:first-child {
          padding: 6px 10px 10px 0;
        }
        &:last-child {
          padding: 6px 0px 10px 10px;
        }
      }
    }
  }
}
.table-sales-report.table__theme-default table {
  td {
    padding: 3px;
    border: 1px solid lightgrey;
  }
}

</style>
<style lang="scss">
  .invoices-links .invoices-modal {
    min-width:185px;
    right:-1px;
  }
  .tooltip.b-tooltip.wide-tooltip .tooltip-inner {
    max-width: 900px !important;
    width: 500px !important;
  }
  .text-editor__block .column-comment {
    max-width: 100%;
  }
  .text-editor__block div.d-inline-flex {
    width: 100%;
  }
  .slide-out__container {
    z-index: 10;
  }
</style>
